import {
  BenefitDocumentDto,
  BenefitDocumentType,
  QualifyingLifeEventType,
} from '@zorro/clients';
import { VALIDATION_MESSAGES } from '@zorro/common-utils';
import {
  createBenefitDocument,
  useMonolithMutation,
} from '@zorro/web/ui-utils';
import * as yup from 'yup';

export const getQleSchemaObject = () =>
  yup.object({
    type: yup
      .string()
      .oneOf(
        Object.values(QualifyingLifeEventType),
        VALIDATION_MESSAGES.optionValid
      )
      .required(),
    occurredOn: yup.date().required(VALIDATION_MESSAGES.dateOfEventRequired),
    otherReason: yup.string().when('type', {
      is: QualifyingLifeEventType.OTHER,
      then: () => yup.string().min(2).max(100).required(),
      otherwise: () => yup.string().optional(),
    }),
    evidence: yup.mixed<(File | BenefitDocumentDto)[]>().optional(),
  });

export type QleFormFields = yup.InferType<
  ReturnType<typeof getQleSchemaObject>
>;

export function useCreateQleBenefitDocuments() {
  const { tryMutate: createBenefitDocumentMutation } = useMonolithMutation({
    method: 'benefitDocumentsControllerCreateBenefitDocument',
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay: false,
  });

  return {
    createQleBenefitDocuments: async (
      employeeId: string,
      employerId: string,
      onboardingPeriodId: string,
      evidence: (File | BenefitDocumentDto)[]
    ) => {
      const qleDocuments = await Promise.all(
        evidence
          .filter((qleEvidence) => qleEvidence instanceof File)
          .map((file) => {
            return createBenefitDocument(
              employeeId,
              employerId,
              file,
              onboardingPeriodId,
              true,
              BenefitDocumentType.QLE
            );
          })
      );
      await Promise.all(
        qleDocuments.map(
          async (document) => await createBenefitDocumentMutation([document])
        )
      );
    },
  };
}

export function useRemoveQleBenefitDocuments() {
  const { tryMutate: removeBenefitDocumentMutation } = useMonolithMutation({
    method: 'benefitDocumentsControllerRemoveBenefitDocument',
    shouldInvalidateQueries: false,
    shouldShowLoadingOverlay: false,
  });

  return {
    removeQleBenefitDocuments: async (
      previousQleDocuments: BenefitDocumentDto[],
      evidence?: (File | BenefitDocumentDto)[]
    ) => {
      const existingQleDocuments = evidence
        ? evidence
            .filter((qleEvidence) => !(qleEvidence instanceof File))
            .map((document) => document as BenefitDocumentDto)
        : [];
      const qleDocumentsToDelete = previousQleDocuments.filter(
        (previousDocument) =>
          !existingQleDocuments.some(
            (existingDocument) => existingDocument.id === previousDocument.id
          )
      );
      await Promise.all(
        qleDocumentsToDelete.map(
          async (document) => await removeBenefitDocumentMutation([document.id])
        )
      );
    },
  };
}
